<script setup lang="ts">
import { onMounted, reactive, Ref, ref, watch, defineAsyncComponent, onBeforeUnmount } from 'vue';
import i18n from '@/localization/i18n';

import BusinessRequestProgressBar from "./BusinessRequestProgressBar.vue";
import CustomChecker from '@/views/components/CustomChecker.vue';
import { storeToRefs } from 'pinia';
import { useBusinessRequestFormStore } from '@/pages/index/stores/business-request-form-store';
import { logNewEvent } from '@/services/modules/firebase';
import { isAuth, validateImageDimensions } from '@/utils/functions';
import { useAlertsStore } from '@/stores/alerts';
import { useUserStore } from '@/stores/user';

const TrashCanOutline = defineAsyncComponent(() => import('vue-material-design-icons/TrashCanOutline.vue'))

const businessRequestFormStore = useBusinessRequestFormStore()
const { imagesFormData, posCode, isFromSeller, source } = storeToRefs(businessRequestFormStore)

const t = i18n.global.t

const number_of_photos = ref(0)
const PHOTO_UPLOAD_LIMIT = 4
const images: Ref<any[]> = ref([])
const image_save_loading = ref(false)
const imageToDelete: Ref<any>= ref(null)
const imageToDeleteIndex: Ref<number | null>= ref(null)
const imgesToDeleteId: Ref<number[]> = ref([])
const imagesFiles:Ref<any[]> = ref([])
const hasChanges: Ref<boolean> = ref(false)
const visibleRef = ref(false)
const indexRef = ref(0)

const addPhoto = ref(false)
const selectedOption = ref<number | null>(null);
const showImagesWaring = ref(false)

const isUserAuthenticated = ref(false)
const isLoading = ref(false)

const userStore = useUserStore()
const user = userStore.getUser
const alerts = useAlertsStore()

onMounted(() => {
    isUserAuthenticated.value = isAuth()
    businessRequestFormStore.wasUserAuthenticatedBeforeStarting = isUserAuthenticated.value ? 1 : 0

    images.value = imagesFormData.value.images
    imagesFiles.value = imagesFormData.value.imagesFiles
    selectedOption.value = imagesFormData.value.selectedOption

    number_of_photos.value = imagesFiles.value.length
})

// dynamic container height to avoid overflow when images are added
const containerHeight: Ref<string> = ref('auto')

function updateContainerHeight() {
    // base heights mirror the previous CSS `.extra-height` values
    const isWide = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 768px)').matches
    const base = isWide ? 550 : 650

    if (!addPhoto.value) {
        containerHeight.value = base + 'px'
        return
    }

    // compute number of rows needed for images (4 items per row)
    const items = Math.max(number_of_photos.value, 1)
    const rows = Math.ceil(items / 4)
    const extra = rows * 130 // ~height per row
    containerHeight.value = (base + extra) + 'px'
}

onMounted(() => {
    updateContainerHeight()
    if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('resize', updateContainerHeight)
    }
})

watch([addPhoto, number_of_photos], () => {
    updateContainerHeight()
})

const unwatchClientConsent = watch(() => imagesFormData.value.client_consent, (newVal) => {
    imagesFormData.value.client_consent = newVal
})

const unwatchImages = watch(images, (newVal) => {
    imagesFormData.value.images = newVal
})

const unwatchImageFiles = watch(imagesFiles, (newVal) => {
    imagesFormData.value.imagesFiles = newVal
    formErrors.noImage = ""
})


const uploadClick = () => {
    const imageInput = document.getElementById("br-image-upload")
    if (imageInput) imageInput.click()
}

function generateRandomString(length = 15) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
}

const deletePhoto = (img: any,index:number) => {
    imageToDelete.value = img
    imageToDeleteIndex.value = index

    onDeletePhoto()
}

const onDeletePhoto = () => {
    if (imageToDelete.value) {
        const img: any = imageToDelete.value
        if(img.type  && img.type === "local"){
            imagesFiles.value.splice(imageToDeleteIndex.value!, 1);
            images.value.splice(imageToDeleteIndex.value!, 1);
            if(number_of_photos.value > 0 ) number_of_photos.value--
            imageToDeleteIndex.value = null
            imageToDelete.value = null
            brImageInput.value = null
            images.value
        }else{
            imgesToDeleteId.value.push(img.id)
            imagesFiles.value.splice(imageToDeleteIndex.value!, 1);
            images.value.splice(imageToDeleteIndex.value!, 1);
        }
        hasChanges.value = true
    }
}

const showImg = (index: number) => {
    indexRef.value = index
    visibleRef.value = true
}


const brImageInput: Ref<HTMLInputElement | null> = ref(null)
const pickImage = async () => {
    if (brImageInput.value && brImageInput.value.files) {
        const file = brImageInput.value.files[0]
        const maxSize = 10 * 1024 * 1024; // 10 MB in bytes

        if (file.size > maxSize) {
            const dismissRequestPostModal = document.getElementById("dismissRequestPostModal")
            dismissRequestPostModal?.click()
            const imageOver10mbModalButton = document.getElementById("imageOver10mbModalButton")
            imageOver10mbModalButton?.click()
            // console.log(`The file "${file.name}" is NOT within the size limit.`);
            return
        }

        try {
            const result = await validateImageDimensions(file)
            // console.log(result)
        } catch(err) {
            // console.log(err)
            if (err === 'image_dimensions_exceed_limit') {
                const dismissRequestPostModal = document.getElementById("dismissRequestPostModal")
                dismissRequestPostModal?.click()
                const imageOver5kPxModalButton = document.getElementById("imageOver5kPxModalButton")
                imageOver5kPxModalButton?.click()
            }
            return
        }
      
        // Generate an object URL from the File
        const previewUrl = URL.createObjectURL(file);
        imagesFiles.value.push({ file: file,image_file: previewUrl, type: 'local'});
        images.value.push(previewUrl)
        image_save_loading.value = false
        number_of_photos.value++
        brImageInput.value!.value = "";
        hasChanges.value = true
    }
}

const next = () =>{
    if(imagesFiles.value.length !== 0 && !addPhoto.value){
        showImagesWaring.value = true 
        return
    }

    if(addPhoto.value && imagesFiles.value.length == 0){
        formErrors.noImage = t('Ajoutez au moins une photo ou image !')
        return
    }
    formErrors.noImage = ""

    // logNewEvent("presta_request_submit_pictures")
    // businessRequestFormStore.next_step('keywords')

    if(isUserAuthenticated.value){
        isLoading.value = true

        let payload = businessRequestFormStore.constructPayload(user, posCode.value, isFromSeller.value, source.value)
        let filesArray = businessRequestFormStore.imagesPayload()

        try {
            businessRequestFormStore.submit(payload,filesArray).then((response) =>{
            if(response !== null){
                alerts.putAlert({ 'code': 'businessRequestSaveChangesFail', 'scope': 'general' })
                isLoading.value = false
                return
            }
            isLoading.value = false
            logNewEvent("presta_request_submit_pictures")
            businessRequestFormStore.next_step('confirmation')
        })
        } catch (error) {
            alerts.putAlert({ 'code': 'businessRequestSaveChangesFail', 'scope': 'general' })
            isLoading.value = false
            return
        }
    }else{
        businessRequestFormStore.next_step('signup')
    }
}

const yesContinue = () =>{
    showImagesWaring.value = false 
    imagesFiles.value = []
    number_of_photos.value = 0
    selectedOption.value = 2
    addPhoto.value = false

    imagesFormData.value.images = []
    imagesFormData.value.imagesFiles = []
    imagesFormData.value.selectedOption = null

    //next()
}

const unwatchSelectedOption = watch(selectedOption, () => {
    addPhoto.value = selectedOption.value == 1 
    imagesFormData.value.selectedOption = selectedOption.value
    formErrors.noImage = ""
    if(selectedOption.value == 2 && imagesFiles.value.length !== 0){
        //show the confirm pop up
        showImagesWaring.value = true
    }
})

const cancel = () =>{
    showImagesWaring.value = false
    addPhoto.value = true 
    selectedOption.value = 1
}

const back = () =>{
    businessRequestFormStore.prev_step('description')
}

const formErrors = reactive({
   noImage: '',
})

const onHide = () => visibleRef.value = false;

onBeforeUnmount(() => {
    unwatchImages()
    unwatchImageFiles()
    unwatchSelectedOption()
    unwatchClientConsent()
    if (typeof window !== 'undefined' && window.removeEventListener) {
        try { window.removeEventListener('resize', updateContainerHeight) } catch (e) { /* noop */ }
    }
})
</script>

<template>
    <div v-if="!showImagesWaring" class="rounded main fullHeight extra-height" :style="{ minHeight: containerHeight }">
        <BusinessRequestProgressBar />
        <div class="fullRemainingHeight">
            <div class="expanded" style="justify-content: space-between;">
                <div class="h-style h3-style use-poppins text-center mt-2">
                    {{ t("w_txt_business_request_having_photos") }}
                </div>
                <div class="mt-3 mx-md-5">
                    <p class="">
                        {{ t("w_txt_business_request_photos_overview") }}
                    </p>
                </div>
                <div class="card shadow rounded-3 mt-3 py-2 px-4 w-100">
                    <div class="card-content">
                        <div class="mb-2 flex-space-between">
                            <div class="form-check">
                                <input v-model="selectedOption"  class="form-check-input" type="radio" name="exampleRadios" id="choices1" value="1">
                                <label class="form-check-label" for="choices1">
                                    {{ t('w_txt_business_request_yes') }}
                                </label>
                            </div>
                        </div>
                        <div class="mb-2 flex-space-between">
                            <div class="form-check">
                                <input v-model="selectedOption" class="form-check-input" type="radio" name="exampleRadios" id="choices2" value="2" >
                                <label class="form-check-label" for="choices2">
                                    {{ t('w_txt_business_request_no_maybe_later') }}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="addPhoto" class="mt-3">
                    <button 
                        v-if="number_of_photos < PHOTO_UPLOAD_LIMIT"
                        @click.stop.prevent="uploadClick"
                        id="select-files" class="btn btn-primary mb-2 custom-action-btn-width-upload-image">
                        <i data-feather="file"></i> 
                            <span v-if="!image_save_loading">{{ t('w_act_csWorkDetail_add_photo') }}</span>
                            <div v-else class="spinner-border spinner-border-sm text-light" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                    </button>
                    <p class="mb-0 text-danger mt-0 error-text ms-2" v-if="formErrors.noImage" >{{ formErrors.noImage }}</p>
                    <input ref="brImageInput" @change.stop.prevent="pickImage" type="file" id="br-image-upload" hidden accept="image/*" />
                    <div style="margin-top: 30px;" v-if="number_of_photos >= PHOTO_UPLOAD_LIMIT"></div>
                    <div class="row mt-3 center-row">
                        <template v-for="(imageFile, index) in imagesFiles">
                            <div class="text-center position-relative image-item-width me-3" >
                                <a href="#" data-toggle="lightbox" data-gallery="example-gallery">
                                    <img  :src="imageFile.image_file" style="width: 100%; height:100%"
                                     class="rounded gallery-image mb-1" 
                                     @click="(e) => {e.preventDefault();showImg(index)}">
                                </a>
                                <br>
                                <span
                                    data-bs-toggle="tooltip" 
                                    :title="t('w_msg_csWorkDetail_deletePhoto')"
                                    style="cursor: pointer; right:-20px" 
                                    class="position-absolute top-0 translate-middle badge rounded-pill">
                                    <trash-can-outline
                                        @click="deletePhoto(imageFile,index)"
                                        class="text-danger" 
                                        style="cursor: pointer;">
                                    </trash-can-outline>
                                </span>
                            </div>
                            <vue-easy-lightbox 
                                :visible="visibleRef" :imgs="images"
                                :index="indexRef" @hide="onHide">
                            </vue-easy-lightbox>
                        </template>
                        <div  v-for="k in 4 - number_of_photos" 
                            class="me-3 imgage-place-holder mb-1 image-item-width"
                            >
                        </div>
                    </div>
                </div>

                <div v-if="posCode" class="card shadow rounded-3 mt-4 py-3 px-3 w-100" style="background-color: #FFF8E1;">
                    <div class="d-flex align-items-center">
                        <CustomChecker
                            id="client_consent"
                            v-model:value="imagesFormData.client_consent"
                            inputLabel="" />
                        <label class="form-check-label ms-3 mb-0 text-start" for="client_consent" style="font-size: 14px;">
                            {{ t("w_txt_leroy_business_request_client_consent") }}
                        </label>
                    </div>
                </div>
            </div>
            <div style="margin-top: auto; margin-bottom:15px ">
                <button @click="back"  type="button" class="btn btn-outline-secondary on-action-btn me-3 mt-2 mb-2 custom-btn-width">
                    {{ t("w_act_business_request_previous") }}
                </button>

                <button @click="next"  
                    type="button" class="btn btn-primary mt-2 mb-2 on-action-btn custom-btn-width"
                    :disabled="imagesFormData.selectedOption === null"
                    :class="{ 'invalid-btn-style': imagesFormData.selectedOption === null}">
                    <!-- {{ t("w_act_business_request_next") }} -->
                    <span v-if="!isLoading">
                        {{ t( isUserAuthenticated ? "w_act_home_business_request_input_submit" : "w_act_business_request_next") }}
                    </span>
                    <span v-else class="spinner-border spinner-border-sm text-light" role="status">
                        <span class="sr-only">Loading...</span>
                    </span>
                </button>
            </div>
        </div>
    </div>
    <div v-else class="rounded main fullHeight">
        <BusinessRequestProgressBar />
        <div class="images-warning">
            <div style="height: 50px;"></div>
            <div class="h-style h3-style use-poppins text-center">
                {{ t("w_txt_business_request_are_you_sur") }}
            </div>
            <div class="mt-4 mx-md-5">
                <p class="">
                    {{ t("w_txt_business_request_will_loose_images") }}
                </p>
            </div>
            <div style="margin-top: 25px; margin-bottom:10px ">
                <button @click="cancel" type="button" class="btn btn-outline-secondary on-action-btn me-3 mt-4 mb-2 custom-btn-width">
                    {{ t("w_act_cancel") }}
                </button>

                <button @click="yesContinue"  type="button" class="btn btn-primary mt-4 mb-2 on-action-btn custom-btn-width">
                    {{ t("w_txt_business_request_yes_continue") }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.btn-primary:focus{
    background-color: #FEB23C !important;
}
.images-warning {
    padding-top: 20px !important;
}
.modal-backdrop.show {
  z-index: 2050 !important;
}
.gallery-image {
    object-fit: cover;
}
.center-row {
    display: flex;
    justify-content: center; 
    align-items: center;    
}

.flex-space-between {
    display: flex;
    justify-content: space-between; 
    align-items: center; 
}

.card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;  
    padding: 1rem;            
}

.card-content{
    width: 100% !important;
}

.card-content p {
    text-align: left;
    margin-bottom: 0px;         
}

.imgage-place-holder{
    background-color: #efefef !important;
}

.main {
    padding-top: 15px;
    position: relative;
}

.bg {
    background-color: #FEB23C !important;
}

.fullHeight {
    height: 100%;
    z-index: 1px;
    border: none !important;
    display: flex;                  
    flex-direction: column;
}

.fullRemainingHeight {
    display: flex;                  
    flex-direction: column; 
    height: 100%;  
}

.expanded{
    flex-grow: 1;
    margin-left: 10px;
    margin-right: 10px;
}

.custom-btn-width {
    min-width: 130px;
}

.extra-height {
    height: 650px !important;
}

.image-item-width{
    width: 120px !important; 
    height:100px
}

/* Medium devices (md) - 768px and up */
@media (min-width: 768px) {
    /* Styles for medium screens */
    .expanded{
        margin-left: 50px !important;
        margin-right: 50px !important;
    }

    .custom-btn-width {
        min-width: 150px !important;
    }

    .extra-height {
        height: 550px !important;
    }

    .image-item-width{
        width: 110px !important; 
        height:100px
    }
}
</style>